import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsApi, contractorsApi, inspectorsApi, settingsApi } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { Card, CardHeader, CardTitle, CardBody, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input, { Textarea, Select, Label } from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import { getToday } from '../lib/dateUtils';
import { ArrowLeft, Save, Plus, Trash2, UserPlus, X, ChevronDown, ChevronUp } from 'lucide-react';

const SELF_OPERATED = 'self';

// Normalize "8:5" / "08:05:00" to "08:05"
function normalizeTime(time) {
  const match = String(time || '').match(/^(\d{1,2}):(\d{1,2})/);
  return match ? `${match[1].padStart(2, '0')}:${match[2].padStart(2, '0')}` : null;
}

// Same rule as the server: 未出工 = no entry/exit time; 加班 = exit later than standard exit time
function classifyWorkItem(item, standardExitTime) {
  const entry = normalizeTime(item.entryTime);
  const exit = normalizeTime(item.exitTime);
  const isNoWork = !entry && !exit;
  const isOvertime = !isNoWork && Boolean((exit && exit > standardExitTime) || (entry && entry >= standardExitTime));
  return { isNoWork, isOvertime };
}

const emptyInspector = {
  inspectorId: null,
  inspectorName: '',
  inspectorPhone: '',
  startTime: '',
  endTime: '',
};

const baseEmptyWorkItem = {
  description: '',
  isSelfOperated: true,
  contractorId: null,
  workerCount: 0,
  entryTime: '',
  exitTime: '',
  status: 'pending',
  remarks: '',
  inspectors: [{ ...emptyInspector }],
  siteManager: '',
  siteManagerPhone: '',
  safetyOfficer: '',
  safetyOfficerPhone: '',
};

export default function ReportFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { user, isContractor, creatableDivisions } = useAuthStore();

  const isEdit = Boolean(id);
  const contractorUser = isContractor();
  const divisionOptions = creatableDivisions();

  // Contractor accounts always report their own contracted work
  const emptyWorkItem = contractorUser
    ? { ...baseEmptyWorkItem, isSelfOperated: false, contractorId: user?.contractorId ?? null }
    : baseEmptyWorkItem;

  const [formData, setFormData] = useState({
    reportDate: getToday(),
    division: divisionOptions.includes(user?.division) ? user.division : (divisionOptions.length === 1 ? divisionOptions[0] : ''),
    projectItemNo: '',
    projectName: '',
    workLocation: '',
    workItems: [{ ...emptyWorkItem }],
  });

  const [errors, setErrors] = useState({});
  const [collapsedItems, setCollapsedItems] = useState({});

  const toggleItemCollapse = (index) => {
    setCollapsedItems((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Fetch contractors
  const { data: contractors } = useQuery({
    queryKey: ['contractors', 'active'],
    queryFn: () => contractorsApi.getAll({ activeOnly: 'true' }),
    select: (res) => res.data,
  });

  // Standard exit time (加班 threshold)
  const { data: standardExitTime } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    select: (res) => res.data?.standardExitTime || '17:00',
  });

  // 平日/假日 determined by the system from the date
  const { data: dayInfo } = useQuery({
    queryKey: ['dayType', formData.reportDate],
    queryFn: () => settingsApi.getDayType(formData.reportDate),
    select: (res) => res.data,
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(formData.reportDate || ''),
  });

  const exitThreshold = standardExitTime || '17:00';

  // Fetch inspectors for autocomplete
  const { data: inspectorsList } = useQuery({
    queryKey: ['inspectors', 'active'],
    queryFn: () => inspectorsApi.getAll({ active: 'true' }),
    select: (res) => res.data?.data || [],
  });

  // Fetch project items for the selected division (for autocomplete)
  const { data: projectItems } = useQuery({
    queryKey: ['projectItems', formData.division],
    queryFn: () => reportsApi.getProjectItems(formData.division),
    select: (res) => res.data || [],
    enabled: !!formData.division,
  });

  // Fetch next project item no when division changes
  const { data: nextProjectItemNo, refetch: refetchNextNo } = useQuery({
    queryKey: ['nextProjectItemNo', formData.division],
    queryFn: () => reportsApi.getNextProjectItemNo(formData.division),
    select: (res) => res.data?.nextProjectItemNo || '',
    enabled: !!formData.division && !isEdit,
  });

  // Fetch existing report for edit
  const { data: existingReport, isLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: () => reportsApi.getById(id),
    select: (res) => res.data,
    enabled: isEdit,
  });

  // Auto-set next projectItemNo when division changes (new report only)
  useEffect(() => {
    if (!isEdit && nextProjectItemNo && formData.division) {
      setFormData((prev) => ({ ...prev, projectItemNo: nextProjectItemNo }));
    }
  }, [nextProjectItemNo, formData.division, isEdit]);

  // Handle projectItemNo change - auto-fill projectName from existing project
  const handleProjectItemNoChange = (value) => {
    setFormData((prev) => {
      const newData = { ...prev, projectItemNo: value };

      // Find matching project item and auto-fill projectName
      if (projectItems && value) {
        const match = projectItems.find((p) => p.projectItemNo === value);
        if (match) {
          newData.projectName = match.projectName;
        }
      }

      return newData;
    });
  };

  // Populate form with existing data
  useEffect(() => {
    if (existingReport) {
      setFormData({
        reportDate: existingReport.reportDate,
        division: existingReport.division,
        projectItemNo: existingReport.projectItemNo || '',
        projectName: existingReport.projectName,
        workLocation: existingReport.workLocation || '',
        workItems: existingReport.workItems?.length > 0
          ? existingReport.workItems.map(item => ({
              id: item.id,
              description: item.description || '',
              isSelfOperated: Boolean(item.isSelfOperated),
              contractorId: item.contractorId || null,
              workerCount: item.workerCount || 0,
              entryTime: item.entryTime || '',
              exitTime: item.exitTime || '',
              status: item.status || 'pending',
              remarks: item.remarks || '',
              inspectors: item.inspectors?.length > 0
                ? item.inspectors.map(insp => ({
                    inspectorId: insp.inspectorId || null,
                    inspectorName: insp.name || insp.inspectorName || '',
                    inspectorPhone: insp.phone || insp.inspectorPhone || '',
                    startTime: insp.startTime || '',
                    endTime: insp.endTime || '',
                  }))
                : [{ ...emptyInspector }],
              siteManager: item.siteManager || '',
              siteManagerPhone: item.siteManagerPhone || '',
              safetyOfficer: item.safetyOfficer || '',
              safetyOfficerPhone: item.safetyOfficerPhone || '',
            }))
          : [{ ...emptyWorkItem }],
      });
    }
  }, [existingReport]);

  // Create/update mutation
  const mutation = useMutation({
    mutationFn: (data) => {
      if (isEdit) {
        return reportsApi.update(id, data);
      }
      return reportsApi.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['reports']);
      navigate('/reports');
    },
    onError: (error) => {
      console.error('Save error:', error);
      if (error.response?.data?.details) {
        const newErrors = {};
        error.response.data.details.forEach((err) => {
          newErrors[err.path?.join('.') || err.field] = err.message;
        });
        setErrors(newErrors);
      }
    },
  });

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // When division changes, reset projectItemNo (will be auto-set by useEffect)
      if (field === 'division' && !isEdit) {
        newData.projectItemNo = '';
        newData.projectName = '';
      }

      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }

    // Refetch next project item no when division changes
    if (field === 'division' && !isEdit) {
      refetchNextNo();
    }
  };

  const handleWorkItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.workItems];
      newItems[index] = { ...newItems[index], [field]: value };

      // If switching to self-operated, clear contractor
      if (field === 'isSelfOperated' && value === true) {
        newItems[index].contractorId = null;
      }

      return { ...prev, workItems: newItems };
    });
  };

  // Contractor select: 自營 is the first option, otherwise a contractor id
  const handleContractorSelect = (index, value) => {
    setFormData((prev) => {
      const newItems = [...prev.workItems];
      newItems[index] = value === SELF_OPERATED
        ? { ...newItems[index], isSelfOperated: true, contractorId: null }
        : { ...newItems[index], isSelfOperated: false, contractorId: value ? Number(value) : null };
      return { ...prev, workItems: newItems };
    });
  };

  const handleInspectorChange = (workItemIndex, inspectorIndex, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.workItems];
      const newInspectors = [...newItems[workItemIndex].inspectors];
      newInspectors[inspectorIndex] = { ...newInspectors[inspectorIndex], [field]: value };
      newItems[workItemIndex] = { ...newItems[workItemIndex], inspectors: newInspectors };
      return { ...prev, workItems: newItems };
    });
  };

  const addInspector = (workItemIndex) => {
    setFormData((prev) => {
      const newItems = [...prev.workItems];
      newItems[workItemIndex] = {
        ...newItems[workItemIndex],
        inspectors: [...newItems[workItemIndex].inspectors, { ...emptyInspector }],
      };
      return { ...prev, workItems: newItems };
    });
  };

  const removeInspector = (workItemIndex, inspectorIndex) => {
    setFormData((prev) => {
      const newItems = [...prev.workItems];
      if (newItems[workItemIndex].inspectors.length <= 1) return prev;
      newItems[workItemIndex] = {
        ...newItems[workItemIndex],
        inspectors: newItems[workItemIndex].inspectors.filter((_, i) => i !== inspectorIndex),
      };
      return { ...prev, workItems: newItems };
    });
  };

  const addWorkItem = () => {
    setFormData((prev) => ({
      ...prev,
      workItems: [...prev.workItems, { ...emptyWorkItem, inspectors: [{ ...emptyInspector }] }],
    }));
  };

  const removeWorkItem = (index) => {
    if (formData.workItems.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      workItems: prev.workItems.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.reportDate) {
      newErrors.reportDate = t('errors.required');
    }
    if (!formData.division) {
      newErrors.division = t('errors.required');
    }
    if (!formData.projectName) {
      newErrors.projectName = t('errors.required');
    }

    // Validate work items
    formData.workItems.forEach((item, index) => {
      if (!item.description) {
        newErrors[`workItems.${index}.description`] = t('errors.required');
      }
      if (!item.isSelfOperated && !item.contractorId) {
        newErrors[`workItems.${index}.contractorId`] = t('errors.required');
      }
      const { isNoWork, isOvertime } = classifyWorkItem(item, exitThreshold);
      if ((isNoWork || isOvertime) && !item.remarks?.trim()) {
        newErrors[`workItems.${index}.remarks`] = isNoWork
          ? t('workItems.noWorkReasonRequired', '未出工請於備註填寫原因')
          : t('workItems.overtimeReasonRequired', '加班請於備註填寫原因');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Expand work items that have errors so the messages are visible
      setCollapsedItems({});
      return;
    }

    // Prepare data
    const submitData = {
      ...formData,
      projectItemNo: formData.projectItemNo || null,
      workItems: formData.workItems.map(item => ({
        ...item,
        workerCount: Number(item.workerCount) || 0,
        contractorId: item.isSelfOperated ? null : item.contractorId,
        inspectors: item.inspectors.filter(insp => insp.inspectorName), // Only submit inspectors with names
      })),
    };

    mutation.mutate(submitData);
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tpc-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit}>
        {/* Project Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/reports')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <CardTitle>
                {isEdit ? t('reports.editReport') : t('reports.newReport')}
              </CardTitle>
            </div>
          </CardHeader>

          <CardBody className="space-y-6">
            {/* Basic Info - Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label required>{t('reports.reportDate')}</Label>
                <Input
                  type="date"
                  value={formData.reportDate}
                  onChange={(e) => handleChange('reportDate', e.target.value)}
                  className={errors.reportDate ? 'border-red-500' : ''}
                />
                {errors.reportDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.reportDate}</p>
                )}
              </div>

              <div>
                <Label>{t('reports.dayTypeAuto', '平日/假日（系統判定）')}</Label>
                <div className="flex items-center h-10 gap-2">
                  {dayInfo && (
                    <Badge className={dayInfo.isHoliday ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                      {dayInfo.isHoliday ? t('dayType.holidayShort', '假日') : t('dayType.weekdayShort', '平日')}
                    </Badge>
                  )}
                  {dayInfo?.name && (
                    <span className="text-sm text-gray-500">{dayInfo.name}</span>
                  )}
                </div>
              </div>

              <div>
                <Label required>{t('reports.division')}</Label>
                <Select
                  value={formData.division}
                  onChange={(e) => handleChange('division', e.target.value)}
                  disabled={divisionOptions.length <= 1}
                  className={errors.division ? 'border-red-500' : ''}
                >
                  <option value="">--</option>
                  {/* Keep an existing report's division selectable even if outside the user's list */}
                  {[...new Set([...divisionOptions, ...(formData.division ? [formData.division] : [])])].map((div) => (
                    <option key={div} value={div}>{div}</option>
                  ))}
                </Select>
                {errors.division && (
                  <p className="text-red-500 text-sm mt-1">{errors.division}</p>
                )}
              </div>
            </div>

            {/* Project Item No & Project Name - Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>{t('reports.projectItemNo')}</Label>
                <Input
                  value={formData.projectItemNo}
                  onChange={(e) => handleProjectItemNoChange(e.target.value)}
                  placeholder="3-1"
                  list="projectItemNos"
                  disabled={!formData.division}
                />
                <datalist id="projectItemNos">
                  {projectItems?.map((p) => (
                    <option key={p.projectItemNo} value={p.projectItemNo}>
                      {p.projectName}
                    </option>
                  ))}
                </datalist>
                <p className="text-xs text-gray-500 mt-1">
                  {t('reports.projectItemNoHint', '選擇現有或輸入新編號')}
                </p>
              </div>

              <div className="md:col-span-3">
                <Label required>{t('reports.projectName')}</Label>
                <Textarea
                  value={formData.projectName}
                  onChange={(e) => handleChange('projectName', e.target.value)}
                  rows={2}
                  className={errors.projectName ? 'border-red-500' : ''}
                  placeholder="瀰力~古亭"
                />
                {errors.projectName && (
                  <p className="text-red-500 text-sm mt-1">{errors.projectName}</p>
                )}
              </div>
            </div>

            <div>
              <Label>{t('reports.workLocation')}</Label>
              <Input
                value={formData.workLocation}
                onChange={(e) => handleChange('workLocation', e.target.value)}
                placeholder="#1A~#6"
              />
            </div>
          </CardBody>
        </Card>

        {/* Work Items Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('workItems.title')}</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addWorkItem}>
                <Plus className="w-4 h-4 mr-1" />
                {t('workItems.add')}
              </Button>
            </div>
          </CardHeader>

          <CardBody className="space-y-4">
            {formData.workItems.map((item, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden"
              >
                {/* Work Item Header - Always Visible */}
                <div
                  className="flex items-center justify-between p-3 bg-gray-100 cursor-pointer hover:bg-gray-150"
                  onClick={() => toggleItemCollapse(index)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="flex-shrink-0 w-6 h-6 bg-tpc-blue text-white rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-700 truncate">
                        {item.description || t('workItems.description')}
                        {classifyWorkItem(item, exitThreshold).isOvertime && (
                          <Badge className="ml-2 bg-blue-100 text-blue-800">{t('workItems.overtimeTag', '加班')}</Badge>
                        )}
                        {classifyWorkItem(item, exitThreshold).isNoWork && (
                          <Badge className="ml-2 bg-gray-200 text-gray-600">{t('workItems.noWorkTag', '未出工')}</Badge>
                        )}
                      </p>
                      {collapsedItems[index] && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {item.isSelfOperated
                            ? t('workItems.selfOperatedOption', '自營')
                            : (contractors?.find((c) => c.id === item.contractorId)?.shortName
                              || contractors?.find((c) => c.id === item.contractorId)?.name
                              || t('workItems.contractor'))}
                          {' | '}
                          {item.workerCount || 0} {t('workItems.workerCount')}
                          {item.inspectors?.filter(i => i.inspectorName).length > 0 && (
                            <> | {item.inspectors.filter(i => i.inspectorName).length} {t('reports.inspector')}</>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {formData.workItems.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); removeWorkItem(index); }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                    {collapsedItems[index] ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Work Item Content - Collapsible */}
                {!collapsedItems[index] && (
                  <div className="p-4 space-y-4">
                    {/* Work Item Description */}
                    <div>
                      <Label required>{t('workItems.description')}</Label>
                      <Textarea
                        value={item.description}
                        onChange={(e) => handleWorkItemChange(index, 'description', e.target.value)}
                        className={errors[`workItems.${index}.description`] ? 'border-red-500' : ''}
                        rows={3}
                        placeholder={t('workItems.descriptionPlaceholder', '輸入工作項目說明，可包含多行內容...')}
                      />
                      {errors[`workItems.${index}.description`] && (
                        <p className="text-red-500 text-sm mt-1">{errors[`workItems.${index}.description`]}</p>
                      )}
                    </div>

                    {/* Contractor & Workers */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label required>{t('reports.contractorType')}</Label>
                        <Select
                          value={item.isSelfOperated ? SELF_OPERATED : (item.contractorId || '')}
                          onChange={(e) => handleContractorSelect(index, e.target.value)}
                          disabled={contractorUser}
                          className={errors[`workItems.${index}.contractorId`] ? 'border-red-500' : ''}
                        >
                          {contractorUser ? (
                            contractors?.filter((c) => c.id === user?.contractorId).map((c) => (
                              <option key={c.id} value={c.id}>{c.shortName || c.name}</option>
                            ))
                          ) : (
                            <>
                              <option value={SELF_OPERATED}>{t('workItems.selfOperatedOption', '自營')}</option>
                              {contractors?.filter(c => c.name !== '台電自營').map((c) => (
                                <option key={c.id} value={c.id}>{c.shortName || c.name}</option>
                              ))}
                            </>
                          )}
                        </Select>
                        {errors[`workItems.${index}.contractorId`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`workItems.${index}.contractorId`]}</p>
                        )}
                      </div>

                      <div>
                        <Label>{t('workItems.workerCount')}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={item.workerCount}
                          onChange={(e) => handleWorkItemChange(index, 'workerCount', e.target.value)}
                        />
                      </div>

                      <div>
                        <Label>{t('workItems.entryTime')}</Label>
                        <Input
                          type="time"
                          value={item.entryTime}
                          onChange={(e) => handleWorkItemChange(index, 'entryTime', e.target.value)}
                        />
                      </div>

                      <div>
                        <Label>{t('workItems.exitTime')}</Label>
                        <Input
                          type="time"
                          value={item.exitTime}
                          onChange={(e) => handleWorkItemChange(index, 'exitTime', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Inspectors Section */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium text-gray-700">{t('reports.inspector')}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addInspector(index)}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          {t('workItems.addInspector')}
                        </Button>
                      </div>

                      {item.inspectors.map((insp, inspIndex) => (
                        <div key={inspIndex} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2 items-end">
                          <div>
                            <Label className="text-[0.9em]">{t('inspectors.name')}</Label>
                            <Input
                              value={insp.inspectorName}
                              onChange={(e) => handleInspectorChange(index, inspIndex, 'inspectorName', e.target.value)}
                              placeholder={t('inspectors.name')}
                              list={`inspectors-${index}-${inspIndex}`}
                            />
                            <datalist id={`inspectors-${index}-${inspIndex}`}>
                              {inspectorsList?.map((i) => (
                                <option key={i.id} value={i.name}>{i.company && `(${i.company})`}</option>
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <Label className="text-[0.9em]">{t('inspectors.phone')}</Label>
                            <Input
                              value={insp.inspectorPhone}
                              onChange={(e) => handleInspectorChange(index, inspIndex, 'inspectorPhone', e.target.value)}
                              placeholder="0912-345-678"
                            />
                          </div>
                          <div>
                            <Label className="text-[0.9em]">{t('workItems.startTime')}</Label>
                            <Input
                              type="time"
                              value={insp.startTime}
                              onChange={(e) => handleInspectorChange(index, inspIndex, 'startTime', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-[0.9em]">{t('workItems.endTime')}</Label>
                            <Input
                              type="time"
                              value={insp.endTime}
                              onChange={(e) => handleInspectorChange(index, inspIndex, 'endTime', e.target.value)}
                            />
                          </div>
                          <div>
                            {item.inspectors.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeInspector(index, inspIndex)}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Site Manager & Safety Officer (only for non-self-operated) */}
                    {!item.isSelfOperated && (
                      <div className="border-t pt-4 mt-4">
                        <Label className="text-sm font-medium text-gray-700 mb-3 block">{t('personnel.title')}</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[0.9em]">{t('personnel.site_manager')}</Label>
                              <Input
                                value={item.siteManager || ''}
                                onChange={(e) => handleWorkItemChange(index, 'siteManager', e.target.value)}
                                placeholder={t('inspectors.name')}
                              />
                            </div>
                            <div>
                              <Label className="text-[0.9em]">{t('inspectors.phone')}</Label>
                              <Input
                                value={item.siteManagerPhone || ''}
                                onChange={(e) => handleWorkItemChange(index, 'siteManagerPhone', e.target.value)}
                                placeholder="0912-345-678"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[0.9em]">{t('personnel.safety_officer')}</Label>
                              <Input
                                value={item.safetyOfficer || ''}
                                onChange={(e) => handleWorkItemChange(index, 'safetyOfficer', e.target.value)}
                                placeholder={t('inspectors.name')}
                              />
                            </div>
                            <div>
                              <Label className="text-[0.9em]">{t('inspectors.phone')}</Label>
                              <Input
                                value={item.safetyOfficerPhone || ''}
                                onChange={(e) => handleWorkItemChange(index, 'safetyOfficerPhone', e.target.value)}
                                placeholder="0912-345-678"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Remarks: reason required for 加班 / 未出工 */}
                    {(() => {
                      const { isNoWork, isOvertime } = classifyWorkItem(item, exitThreshold);
                      const remarkError = errors[`workItems.${index}.remarks`];
                      return (
                        <div>
                          <Label required={isNoWork || isOvertime}>
                            {t('workItems.remarksReason', '備註（未出工/加班原因）')}
                          </Label>
                          <Textarea
                            value={item.remarks || ''}
                            onChange={(e) => handleWorkItemChange(index, 'remarks', e.target.value)}
                            rows={2}
                            className={remarkError ? 'border-red-500' : ''}
                            placeholder={
                              isNoWork
                                ? t('workItems.noWorkReasonPlaceholder', '未出工原因，例如：天候不佳、停電取消...')
                                : isOvertime
                                  ? t('workItems.overtimeReasonPlaceholder', '加班原因，例如：趕工、搶修...')
                                  : t('workItems.remarksPlaceholder', '備註說明...')
                            }
                          />
                          {remarkError && <p className="text-red-500 text-sm mt-1">{remarkError}</p>}
                          <p className="text-xs text-gray-500 mt-1">
                            {t('workItems.timeRuleHint', '收工時間晚於 {{time}} 為加班；出工及收工時間皆空白為未出工', { time: exitThreshold })}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </CardBody>

          <CardFooter>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/reports')}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" isLoading={mutation.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {t('common.save')}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
